import { type ReactNode } from 'react';

import { Button, ContentWithTooltip, RadioGroup, Text } from '@ekhodealer/ekho-common/rev';

import Info16 from '@common/components/icons/Info16';

export interface FiltersDrawerRadioGroupItem {
	value: string;
	label: string;
}

interface FiltersDrawerRadioGroupProps {
	/** The label/title for the radio group */
	label: string | ReactNode;
	/** Array of items to display as radio buttons */
	items: FiltersDrawerRadioGroupItem[];
	/** Controlled value - single selected item value. Pass null to deselect all. */
	value?: string | null;
	/** Callback when selection changes */
	onChange?: (value: string) => void;
	/** Callback to clear the current selection. When provided and a value is selected, a "Clear" link is shown next to the label. */
	onClear?: () => void;
	/** Whether the radio group is disabled */
	isDisabled?: boolean;
	/** Optional tooltip help text to display next to the label */
	tooltipHelpText?: string;
}

/**
 * FiltersDrawerRadioGroup component that provides a reusable radio group
 * with consistent styling for filter sections
 */
export function FiltersDrawerRadioGroup({
	label,
	items,
	value,
	onChange,
	onClear,
	isDisabled,
	tooltipHelpText,
}: FiltersDrawerRadioGroupProps) {
	const showClear = onClear && value;

	const hasLabelText = typeof label === 'string' ? label.length > 0 : !!label;
	const hasVisibleLabel = hasLabelText || tooltipHelpText || showClear;

	const labelContent = hasLabelText
		? typeof label === 'string'
			? (
				<Text size='bodySmall' weight='semibold'>
					{label}
				</Text>
			)
			: label
		: null;

	const labelWithClear = hasVisibleLabel ? (
		<div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
			{labelContent}
			{tooltipHelpText && (
				<ContentWithTooltip
					content={
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<Info16 />
						</div>
					}
					tooltipContent={tooltipHelpText}
				/>
			)}
			{showClear && (
				<Button variant='underlined' size='small' onPress={onClear}>
					Clear
				</Button>
			)}
		</div>
	) : '';

	return (
		<RadioGroup
			label={labelWithClear}
			value={value}
			onChange={onChange}
			isDisabled={isDisabled}
			items={items.map((item) => ({
				value: item.value,
				label: <Text size='bodySmall'>{item.label}</Text>,
			}))}
		/>
	);
}
